import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule, DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { CompaniesService } from '@app/services/companies.service';
import { Subject, takeUntil, interval, take, switchMap } from 'rxjs';

// ──── Confirmation dialog (inline to avoid separate-file compilation issues) ────
@Component({
    selector: 'confirm-mark-done-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
    template: `
        <div class="p-6">
            <div class="flex items-center gap-3 mb-4">
                <div class="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100">
                    <mat-icon class="text-amber-600 icon-size-6" [svgIcon]="'heroicons_outline:exclamation-triangle'"></mat-icon>
                </div>
                <h2 class="text-lg font-semibold m-0">{{ data.title }}</h2>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">{{ data.message }}</p>
            <div class="flex justify-end gap-2">
                <button mat-stroked-button (click)="dialogRef.close(false)">No</button>
                <button mat-flat-button color="primary" (click)="dialogRef.close(true)">Yes</button>
            </div>
        </div>
    `,
})
export class ConfirmMarkDoneDialogComponent {
    constructor(
        public dialogRef: MatDialogRef<ConfirmMarkDoneDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { title: string; message: string },
    ) {}
}

@Component({
    selector       : 'notifications',
    templateUrl    : './notifications.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'notifications',
    standalone     : true,
    imports        : [MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, NgTemplateOutlet, DatePipe, TranslateModule, MatDialogModule],
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    waitingCount: number = 0;
    notificationView: 'open' | 'waiting' | 'all' = 'open';
    notificationGroupBy: 'student' | 'ungrouped' = 'student';
    notificationPage: number = 1;
    notificationPerPage: number = 20;
    notificationTotal: number = 0;
    // For grouped-by-student view: client-side group pagination
    groupPage: number = 1;
    readonly GROUP_PER_PAGE = 10;
    // Currently selected company (used to filter notifications)
    selectedCompanyId: number | null = null;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _refreshCount$ = new Subject<void>();

    private _debug(label: string, payload?: any, color: string = '#0891b2'): void
    {
        try {
            // eslint-disable-next-line no-console
            console.log(`%c[NotificationsComponent] ${label}`, `color:${color};font-weight:700`, payload ?? '');
        } catch {
            // no-op
        }
    }

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _companiesService: CompaniesService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router,
        private _dialog: MatDialog,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this._refreshCount$
            .pipe(
                switchMap(() => this._notificationsService.getUnreadCount(this.selectedCompanyId)),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((count: number) => {
                this.unreadCount = count;
                this._debug('unread count refreshed', { count, companyId: this.selectedCompanyId }, '#0e7490');
                this._changeDetectorRef.markForCheck();
            });

        this._companiesService.selectedCompanyId$
            .pipe(
                switchMap((companyId) => this._notificationsService.getWaitingCount(companyId)),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((count: number) => {
                this.waitingCount = count;
                this._changeDetectorRef.markForCheck();
            });

        // Track selected company and reload when it changes.
        // BehaviorSubject emits the current value immediately, so this also handles the initial load.
        this._companiesService.selectedCompanyId$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((companyId: number | null) => {
                this.selectedCompanyId = companyId;
                this.notificationPage = 1;
                this.groupPage = 1;
                this._loadNotifications();
                this._refreshCount$.next();
            });

        this._refreshCount$.next();

        // Poll every 30 seconds
        interval(30000)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._refreshCount$.next();
                this._loadNotifications(false);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // If already attached, detach first to avoid "Host already has a portal attached" error
        if ( this._overlayRef.hasAttached() )
        {
            this._overlayRef.detach();
            return;
        }

        // Always open with "Open" filter on first open.
        this.notificationView = 'open';
        this.notificationPage = 1;
        this.groupPage = 1;
        this._loadNotifications();

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        if (this._overlayRef?.hasAttached()) {
            this._overlayRef.detach();
        }
    }

    /**
     * Show confirmation dialog, then mark ALL notifications as done (read).
     */
    confirmMarkAllAsDone(): void
    {
        if (this.notificationView === 'open') {
            return;
        }
        const dialogRef = this._dialog.open(ConfirmMarkDoneDialogComponent, {
            width: '420px',
            data: {
                title: 'Mark all as Done',
                message: 'All notifications will be marked as done. The underlying supervisor tasks will NOT be completed — the students will still be waiting for an answer. No notification will be sent to the students.',
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) {
                return;
            }
            this._debug('markAllAsDone() confirmed', {
                currentUnread: this.unreadCount,
                visibleNotifications: this.notifications?.length || 0,
            }, '#16a34a');
            this._notificationsService.markAllAsRead().subscribe(() => {
                this.notificationPage = 1;
                this._loadNotifications();
                this._debug('markAllAsDone() completed', {}, '#15803d');
            });
        });
    }

    /**
     * Show confirmation dialog, then mark a single notification as done (read).
     */
    confirmMarkAsDone(notification: Notification): void
    {
        if (this.notificationView === 'open') {
            return;
        }
        const dialogRef = this._dialog.open(ConfirmMarkDoneDialogComponent, {
            width: '420px',
            data: {
                title: 'Mark as Done',
                message: 'This notification will be marked as done. The underlying supervisor task will NOT be completed — the student will still be waiting for an answer. No notification will be sent to the student.',
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) {
                return;
            }
            this._debug('markAsDone() confirmed', {
                id: notification?.id,
                readBefore: notification?.read,
            }, '#d97706');
            notification.read = true;
            this._notificationsService.update(notification.id, notification).subscribe((updated) => {
                if (!updated.read) {
                    notification.read = false;
                    this._changeDetectorRef.markForCheck();
                    return;
                }
                // Subscribe to the optimistically-updated count so the bell badge reflects it.
                this._notificationsService.unreadCount$
                    .pipe(takeUntil(this._unsubscribeAll), take(1))
                    .subscribe((count: number) => {
                        this.unreadCount = count;
                        this._changeDetectorRef.markForCheck();
                    });
                // Reload the list without overriding the optimistic counter.
                this._loadNotifications(false);
                this._debug('markAsDone() completed', { id: notification.id }, '#16a34a');
            });
        });
    }

    setNotificationView(view: 'open' | 'waiting' | 'all'): void
    {
        if (this.notificationView === view) {
            return;
        }
        this.notificationView = view;
        this.notificationPage = 1;
        this.groupPage = 1;
        this._loadNotifications();
    }

    setNotificationGroupBy(groupBy: 'student' | 'ungrouped'): void
    {
        if (this.notificationGroupBy === groupBy) {
            return;
        }
        this.notificationGroupBy = groupBy;
        this.notificationPage = 1;
        this.groupPage = 1;
        // Grouped mode loads all at once; ungrouped uses normal pagination
        this._loadNotifications();
    }

    /**
     * Group ALL loaded notifications by student_id (no pagination — groups are paginated separately)
     */
    get notificationsByStudent(): { studentId: number; studentName: string; companyName?: string; count: number; notifications: Notification[] }[]
    {
        if (!this.notifications?.length) {
            return [];
        }
        const map = new Map<number, { studentName: string; companyName?: string; notifications: Notification[] }>();
        for (const n of this.notifications) {
            const sid = n.student_id ?? 0;
            if (!map.has(sid)) {
                map.set(sid, {
                    studentName: n.student_name || 'Unknown',
                    companyName: n.company_name,
                    notifications: [],
                });
            }
            const entry = map.get(sid)!;
            entry.notifications.push(n);
            if (n.student_name) entry.studentName = n.student_name;
            if (n.company_name) entry.companyName = n.company_name;
        }
        return Array.from(map.entries())
            .map(([studentId, data]) => ({
                studentId,
                studentName: data.studentName,
                companyName: data.companyName,
                count: data.notifications.length,
                notifications: data.notifications,
            }))
            .sort((a, b) => (b.count - a.count) || (a.studentName || '').localeCompare(b.studentName || ''));
    }

    /** Paginated slice of student groups for display */
    get pagedNotificationsByStudent(): { studentId: number; studentName: string; companyName?: string; count: number; notifications: Notification[] }[]
    {
        const all = this.notificationsByStudent;
        const start = (this.groupPage - 1) * this.GROUP_PER_PAGE;
        return all.slice(start, start + this.GROUP_PER_PAGE);
    }

    get groupTotalPages(): number
    {
        return Math.max(1, Math.ceil(this.notificationsByStudent.length / this.GROUP_PER_PAGE));
    }

    previousGroupPage(): void
    {
        if (this.groupPage > 1) {
            this.groupPage--;
            this._changeDetectorRef.markForCheck();
        }
    }

    nextGroupPage(): void
    {
        if (this.groupPage < this.groupTotalPages) {
            this.groupPage++;
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Navigate to supervisor for a student group (first notification's course)
     */
    onStudentGroupClick(group: { studentId: number; notifications: Notification[] }): void
    {
        const first = group.notifications[0];
        if (first && first.student_id && first.student_course_id) {
            this.closePanel();
            if (first.company_id) {
                const currentCompanyId = this._companiesService.getSelectedCompanyId();
                if (currentCompanyId !== first.company_id) {
                    this._companiesService.selectCompany(first.company_id);
                }
            }
            this._router.navigate(['/supervisor', first.student_id, first.student_course_id]);
        }
    }

    previousNotificationPage(): void
    {
        if (this.notificationPage <= 1) {
            return;
        }
        this.notificationPage--;
        this._loadNotifications();
    }

    nextNotificationPage(): void
    {
        if (this.notificationPage >= this.notificationTotalPages) {
            return;
        }
        this.notificationPage++;
        this._loadNotifications();
    }

    get notificationTotalPages(): number
    {
        return Math.max(1, Math.ceil((this.notificationTotal || 0) / this.notificationPerPage));
    }

    /**
     * Map actiontype_id to the route path used in the secondary outlet
     */
    private static readonly ACTION_TYPE_ROUTES: { [key: number]: string } = {
        1: 'watch-video',
        2: 'read-text',
        3: 'read-pdf',
        4: 'write-essay',
        5: 'do-drill',
        6: 'checkout',
        7: 'course-start',
        8: 'completion',
        9: 'drill-skype',
        10: 'clear-word',
        11: 'read-policy',
        12: 'youtube',
        13: 'quiz',
        14: 'final-test',
        15: 'how-are-you',
        16: 'twinning',
        17: 'supervisor',
        18: 'gallery',
    };

    /**
     * Navigate to the relevant supervisor page when clicking a notification
     * URL: /supervisor/{student_id}/{student_course_id}/(secondary:{step-route};scid={scid};step_id={scs_id})
     */
    onNotificationClick(notification: Notification): void
    {
        this._debug('onNotificationClick()', {
            id: notification?.id,
            read: notification?.read,
            student_id: notification?.student_id,
            student_course_id: notification?.student_course_id,
            student_course_step_id: notification?.student_course_step_id,
            actiontype_id: notification?.actiontype_id,
            attention_state: notification?.attention_state,
        }, '#2563eb');
        // Navigate without marking as done on the needs-review queue.
        if (notification.student_id && notification.student_course_id && notification.useRouter) {
            this.closePanel();

            // Auto-switch to the student's company so the sidebar loads the correct student list
            if (notification.company_id) {
                const currentCompanyId = this._companiesService.getSelectedCompanyId();
                if (currentCompanyId !== notification.company_id) {
                    this._companiesService.selectCompany(notification.company_id);
                }
            }

            const stepRoute = notification.actiontype_id
                ? (NotificationsComponent.ACTION_TYPE_ROUTES[notification.actiontype_id] || 'none')
                : 'none';

            // Build the navigation with secondary outlet
            // Result: /supervisor/{student_id}/{student_course_id}/(secondary:{stepRoute};scid={scid};step_id={scs_id})
            this._router.navigate([
                '/supervisor',
                notification.student_id,
                notification.student_course_id,
                {
                    outlets: {
                        secondary: [
                            stepRoute,
                            {
                                scid: notification.student_course_id,
                                step_id: notification.student_course_step_id || 0,
                            },
                        ],
                    },
                },
            ]);
        } else if (notification.link && notification.useRouter) {
            this.closePanel();
            this._router.navigate([notification.link]);
        } else if (notification.link) {
            this.closePanel();
            window.open(notification.link, '_blank');
        }
    }

    /**
     * Build a detailed tooltip string for a notification
     */
    buildTooltip(notification: Notification): string
    {
        const lines: string[] = [];

        if (notification.student_name) {
            lines.push(`Student: ${notification.student_name}`);
        }
        if (notification.course_title) {
            lines.push(`Course: ${notification.course_title}`);
        }
        if (notification.step_sort && notification.step_title) {
            lines.push(`Step #${notification.step_sort}: ${notification.step_title}`);
        } else if (notification.step_title) {
            lines.push(`Step: ${notification.step_title}`);
        }
        if (notification.actiontype_id) {
            const typeLabel = NotificationsComponent.ACTION_TYPE_LABELS[notification.actiontype_id];
            if (typeLabel) {
                lines.push(`Type: ${typeLabel}`);
            }
        }
        if (notification.company_name) {
            lines.push(`Company: ${notification.company_name}`);
        }

        return lines.join('\n');
    }

    /**
     * Human-readable action type labels (for tooltips)
     */
    private static readonly ACTION_TYPE_LABELS: { [key: number]: string } = {
        1: 'Watch Video',
        2: 'Read Text',
        3: 'Read PDF',
        4: 'Write Essay',
        5: 'Do Drill',
        6: 'Checkout',
        7: 'Course Start',
        8: 'Completion',
        9: 'Video Drill',
        10: 'Clear Word',
        11: 'Read Policy',
        12: 'YouTube',
        13: 'Quiz',
        14: 'Final Test',
        15: 'How Are You',
        16: 'Twinning',
        17: 'Supervisor Drill',
        18: 'Gallery',
    };

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    trackGroupByFn(index: number, group: { studentId: number }): number
    {
        return group.studentId;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }

    canMarkNotificationDone(): boolean
    {
        return this.notificationView !== 'open';
    }

    private _refreshWaitingCount(): void
    {
        this._notificationsService.getWaitingCount(this.selectedCompanyId)
            .pipe(takeUntil(this._unsubscribeAll), take(1))
            .subscribe((count) => {
                this.waitingCount = count;
                this._changeDetectorRef.markForCheck();
            });
    }

    private _loadNotifications(refreshCounter: boolean = true): void
    {
        // In grouped mode fetch all notifications at once so grouping is correct across pages.
        // In ungrouped mode use normal per-page pagination.
        const perPage = this.notificationGroupBy === 'student' ? 1000 : this.notificationPerPage;
        const page = this.notificationGroupBy === 'student' ? 1 : this.notificationPage;
        this._debug('_loadNotifications() request', {
            view: this.notificationView,
            page,
            perPage,
            companyId: this.selectedCompanyId,
            groupBy: this.notificationGroupBy,
        }, '#7c3aed');
        this._notificationsService.getAll({
            view: this.notificationView,
            page,
            perPage,
            companyId: this.selectedCompanyId,
        })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                this.notifications = result.items || [];
                this.notificationTotal = Number(result.total || 0);
                this._debug('_loadNotifications() result', {
                    total: this.notificationTotal,
                    loaded: this.notifications.length,
                    view: this.notificationView,
                    page: this.notificationPage,
                }, '#8b5cf6');
                if (refreshCounter) {
                    this._refreshCount$.next();
                    this._refreshWaitingCount();
                } else {
                    // Counter was already updated optimistically; just trigger CD.
                    this._changeDetectorRef.markForCheck();
                }
            });
    }
}
